import {useEffect, useRef, useState} from "react";

type UseAutoRetryOptions = {
    shouldRetry: boolean;
    retryCallback: () => void;
    maxAttempts?: number;
    retryDelayInMs?: number;
    maxDelayInMs?: number;
};

export const useAutoRetry = ({
                                 shouldRetry,
                                 retryCallback,
                                 maxAttempts = 3,
                                 retryDelayInMs = 1000,
                                 maxDelayInMs = 10000,
                             }: UseAutoRetryOptions): [number, boolean, number, () => void] => {
    const [retryCount, setRetryCount] = useState(0);
    const [retrying, setRetrying] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(0);
    const intervalRef = useRef<any | null>(null);
    const timeoutRef = useRef<any | null>(null);

    useEffect(() => {
        if (shouldRetry && retryCount < maxAttempts) {
            const delay = Math.min(retryDelayInMs * 2 ** retryCount, maxDelayInMs);
            let currentSecond = Math.ceil(delay / 1000);

            setRetrying(true);
            setSecondsRemaining(currentSecond);

            intervalRef.current = setInterval(() => {
                currentSecond -= 1;
                setSecondsRemaining(Math.max(currentSecond, 0));
            }, 1000);

            timeoutRef.current = setTimeout(() => {
                clearInterval(intervalRef.current!);
                setRetryCount(prev => prev + 1);
                retryCallback();
            }, delay);

            return () => {
                clearTimeout(timeoutRef.current!);
                clearInterval(intervalRef.current!);
            };
        } else {
            setRetrying(false);
            setSecondsRemaining(0);
            clearInterval(intervalRef.current!);
        }
    }, [shouldRetry, retryCount, retryCallback, maxAttempts, retryDelayInMs, maxDelayInMs]);

    const reset = () => {
        setRetryCount(0);
        setSecondsRemaining(0);
        clearTimeout(timeoutRef.current!);
        clearInterval(intervalRef.current!);
    };

    return [retryCount, retrying, secondsRemaining, reset];
};